/**
 * Kanban deal board — drag-and-drop lead status pipeline.
 * Uses native HTML5 drag-and-drop (no extra deps).
 */
import { useEffect, useState } from "react";
import { api } from "../lib/api";

export default function KanbanBoard({ onOpenLead }) {
  const [data, setData] = useState(null);
  const [drag, setDrag] = useState(null);

  const load = async () => {
    const { data: r } = await api.get("/dealer/kanban");
    setData(r);
  };
  useEffect(() => { load(); }, []);

  const onDrop = async (status) => {
    if (!drag) return;
    const fromStatus = drag.from;
    const id = drag.id;
    setDrag(null);
    if (fromStatus === status) return;
    let reason = null;
    if (["sold", "lost", "bad"].includes(status)) {
      reason = window.prompt(`Why ${status}?`);
      if (reason === null) return;
    }
    // Optimistic UI
    setData((d) => {
      if (!d) return d;
      const items = { ...d.items };
      items[fromStatus] = (items[fromStatus] || []).filter((x) => x.id !== id);
      const moved = (data?.items?.[fromStatus] || []).find((x) => x.id === id);
      if (moved) items[status] = [{ ...moved, status }, ...(items[status] || [])];
      return { ...d, items };
    });
    try {
      await api.patch(`/dealer/lead/${id}/kanban-move`, { status, reason });
    } catch (ex) {
      window.alert("Couldn't move: " + (ex?.response?.data?.detail || "error"));
      load();
    }
  };

  if (!data) return <div className="text-white/60 p-6">Loading pipeline…</div>;

  return (
    <div className="overflow-x-auto pb-3" data-testid="kanban-board">
      <div className="flex gap-3 min-w-max">
        {data.stages.map((st) => {
          const items = data.items[st.v] || [];
          return (
            <div key={st.v}
                 className="w-72 shrink-0 rounded-2xl bg-white/5 border border-white/10 flex flex-col"
                 onDragOver={(e) => e.preventDefault()}
                 onDrop={() => onDrop(st.v)}
                 data-testid={`kanban-col-${st.v}`}>
              <div className="px-3 py-2 border-b border-white/10 flex items-center justify-between sticky top-0 bg-white/5 backdrop-blur rounded-t-2xl">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full" style={{ background: st.color }} />
                  <div className="text-xs font-extrabold uppercase tracking-widest text-white">{st.label}</div>
                </div>
                <div className="text-xs text-white/60 font-bold">{items.length}</div>
              </div>
              <div className="p-2 space-y-2 max-h-[640px] overflow-y-auto">
                {items.length === 0 && <div className="text-[11px] text-white/40 px-2 py-3">Drop a lead here.</div>}
                {items.map((l) => (
                  <Card key={l.id} lead={l} onClick={() => onOpenLead?.(l.id)}
                        onDragStart={() => setDrag({ id: l.id, from: st.v })} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Card({ lead, onClick, onDragStart }) {
  const hot = lead.ai_intent_score >= 80;
  const stale = (lead.days_in_stage || 0) >= 7;
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onClick={onClick}
      data-testid={`kanban-card-${lead.id}`}
      className={`rounded-xl bg-white p-3 cursor-grab active:cursor-grabbing border-l-4 hover:shadow-lg transition-shadow ${
        hot ? "border-red-500" : stale ? "border-amber-500" : "border-[var(--ssc-blue)]"
      }`}
    >
      <div className="font-extrabold text-[var(--ssc-navy)] text-sm leading-tight">{lead.name || "Anonymous"}</div>
      {lead.vehicle_title && <div className="text-[11px] text-[#475569] mt-0.5 line-clamp-1">{lead.vehicle_title}</div>}
      <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
        {hot && <span className="text-[9px] font-extrabold uppercase tracking-widest bg-red-500 text-white px-1.5 py-0.5 rounded-full">🔥 {lead.ai_intent_score}</span>}
        {!hot && lead.ai_intent_score != null && <span className="text-[9px] font-bold text-[#6b7c93]">Score {lead.ai_intent_score}</span>}
        {stale && <span className="text-[9px] font-extrabold uppercase tracking-widest bg-amber-500 text-white px-1.5 py-0.5 rounded-full">{lead.days_in_stage}d</span>}
        {(lead.tags || []).slice(0, 2).map((t) => (
          <span key={t} className="text-[9px] font-bold text-[var(--ssc-blue)] bg-[var(--ssc-blue)]/10 px-1.5 py-0.5 rounded-full border border-[var(--ssc-blue)]/30">{t}</span>
        ))}
      </div>
    </div>
  );
}
